<template>
<div>
    <div class="default-settings-title">
        <span class="account-name">Step 1: Choose a server</span>
        <span class="default-settings-subtitle">The server containing the files you want to backup.</span>
    </div>

    <div class="columns is-multiline">

        <!-- <div class="column is-full">
            <div class="notification is-danger" style="font-size: 13px;">
                When an existent server is selected, check the SSH connection. If it's ok, skip step 2. If not, associate the new SSH key to the server.
            </div>
        </div> -->

        <div class="column is-full" style="margin-top: 8px;">
            <!-- <div style="color: #353b48; margin-bottom: 8px;">Or select an existent one</div> -->
            <div class="columns is-multiline">
                <div class="column is-half" @click="selectAction('new_server')">
                    <div class="server-widget server-widget-hoverable-border" :class="{ 'server-widget-selected-border': actionIsSelected('new_server') }">
                        <div class="add-new-server">
                            <div>
                                <font-awesome-icon icon="plus-circle"/>&nbsp;&nbsp;Add new server</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="column is-half" @click="selectAction('existent_server')">
                    <div class="server-widget server-widget-hoverable-border" :class="{ 'server-widget-selected-border': actionIsSelected('existent_server') }">
                        <div class="add-new-server">
                            <div>
                                <font-awesome-icon icon="server"/>&nbsp;&nbsp;Choose an existent server</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="column is-full has-text-centered" v-if="actionIsSelected('existent_server') && loading">
                    <img src="/loader-dark.svg">
                </div>
                <div class="column is-full" v-if="actionIsSelected('existent_server') && !hasServers && !loading">
                    <div class="notification" style="font-size: 13px;">
                        You don't have any existent servers. Try to add one and it will appear here next time.
                    </div>
                </div>
                <div class="column is-full" v-if="actionIsSelected('existent_server') && hasServers && !loading">
                    <div class="columns is-multiline">
                        <div class="column is-4" v-for="server in servers.data" v-bind:key="server.id">
                            <server-mini-widget :server="server" :selected-server-id="selectedServerId"></server-mini-widget>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="column is-full" v-if="actionIsSelected('new_server')">
            <div class="columns is-multiline">
                <div class="column is-full">
                    <div class="field" @click="toggleSetupType">
                        <input id="switchColorInfo" type="checkbox" v-model="autoSetup" name="switchMode" class="switch is-info">
                        <label for="switchMode">{{ setupLabel }}</label>
                    </div>
                </div>
                <div class="column is-full" v-if="autoSetup && serverSetupToken && !loadingServerSetupToken">

                    <div v-if="autoSetupError" class="notification is-danger" style="font-size: 13px; margin-bottom: 20px;">{{ autoSetupError }}</div>

                    <span style="display:block;margin-top:0px;margin-bottom:10px; font-size: 14px; font-weight:bold;">Run the following shell command on your server and it will be automatically added to your Weap.io dashboard.</span>
                    <div class="notification is-info" style="font-size: 13px; margin-bottom: 15px; background-color: #25303F !important;">
                        bash -c "$(curl -sSL 'https://api.weap.io/api/scripts/store_server/{{serverSetupToken}}')"
                    </div>
                </div>
                <div class="column is-full has-text-centered" v-if="autoSetup && loadingServerSetupToken">
                    <img src="/loader-dark.svg">
                </div>
                <div class="column is-full" v-if="!autoSetup">
                    <div class="columns is-multiline">
                        <div class="column is-full">
                            <span style="display:block;font-size: 14px; font-weight:bold;">Manually type the server details.</span>
                        </div>
                        <div class="column is-6">
                            <div class="field">
                                <input v-model="newServer.name" @keyup.enter="nextStep" type="text" :class="{ 'is-danger': hasError('name') }" class="input custom-input-size is-medium" placeholder="Friendly name" autocomplete="off" :disabled="storingServer" />
                                <span v-if="!hasError('name')" class="help">A friendly name to easily recognize the server later.</span>
                                <span v-if="hasError('name')" class="help is-danger">{{ getError('name') }}</span>
                            </div>
                        </div>
                        <div class="column is-6">
                            <div class="field">
                                <input v-model="newServer.user" @keyup.enter="nextStep" type="text" :class="{ 'is-danger': hasError('user') }" class="input custom-input-size is-medium" placeholder="Server user" autocomplete="off" :disabled="storingServer" />
                                <span v-if="!hasError('user')" class="help">This user will be used to take the backups.</span>
                                <span v-if="hasError('user')" class="help is-danger">{{ getError('user') }}</span>
                            </div>
                        </div>

                        <div class="column is-6">
                            <div class="field">
                                <input v-model="newServer.host" @keyup.enter="nextStep" type="text" :class="{ 'is-danger': hasError('host') }" class="input custom-input-size is-medium" placeholder="11.12.13.14" autocomplete="off" :disabled="storingServer" />
                                <span v-if="!hasError('host')" class="help">The IP or hostname of your server.</span>
                                <span v-if="hasError('host')" class="help is-danger">{{ getError('host') }}</span>
                            </div>
                        </div>
                        
                        <div class="column is-6">
                            <div class="field">
                                <input v-model="newServer.port" @keyup.enter="nextStep" type="text" :class="{ 'is-danger': hasError('port') }" class="input custom-input-size is-medium" placeholder="22" autocomplete="off" :disabled="storingServer" />
                                <span v-if="!hasError('port')" class="help">Server port. Usually, the default one is 22.</span>
                                <span v-if="hasError('port')" class="help is-danger">{{ getError('port') }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="columns is-multiline">
        <div class="column is-3">
            <div @click="nextStep" class="button custom-button-size is-fullwidth is-info" :class="{ 'is-loading': storingServer }" :disabled="nextButtonIsDisabled">Next</div>
        </div>
        <div class="column is-3">
            <div @click="close" class="close-add-monitor-modal button custom-button-size is-fullwidth is-danger" :disabled="cancelButtonIsDisabled">Cancel</div>
        </div>
    </div>
</div>
</template>

<script>
import Api from '@/api';
import FormErrorsMixin from '@/mixins/FormErrors';
import ServerMiniWidget from '@/components/Servers/ServerMiniWidget';

export default {
    mixins: [FormErrorsMixin],

    data() {
        return {
            selectedAction: 'new_server',
            newServer: {
                name: '',
                host: '',
                user: '',
                port: 22,
            },
            servers: null,
            serversFetchedOnce: false,
            allowedInputs: ['name', 'user', 'host', 'port'],
            selectedServer: null,
            loading: false,
            storingServer: false,
            autoSetup: true,
            serverSetupToken: null,
            loadingServerSetupToken: false,
            autoSetupError: '',
        }
    },

    components: {
        ServerMiniWidget,
    },

    mounted() {
        this.getUserFirstAvailableServerSetup();
        this.listenEvents();
    },

    methods: {
        selectAction(action) {
            this.selectedAction = action;

            if (action == 'new_server' && this.autoSetup) {
                this.getUserFirstAvailableServerSetup();
            }
        },

        actionIsSelected(action) {
            return this.selectedAction == action;
        },

        nextStep() {
            if (this.nextButtonIsDisabled) {
                return;
            }

            if (this.actionIsSelected('new_server')) {
                if (this.autoSetup) {
                    Api().get('users/current/servers/' + this.serverSetupToken)
                        .then((success) => {
                            this.selectedServer = success.data.data;
                            this.moveToNextStep();
                        })
                        .catch((error) => {
                            if (error.response.status == 404) {
                                this.autoSetupError = 'The command was not successfully exectued on a server. Try again after successfully running it.';
                            }
                        });
                } else {
                    this.storeNewServer(() => {
                        Api().get('users/current/servers/last')
                            .then((success) => {
                                this.selectedServer = success.data.data;
                                this.moveToNextStep();
                            })
                            .catch((error) => {
                                //
                            });
                    });
                }
            } else {
                this.moveToNextStep(this.selectedServer);
            }
        },

        moveToNextStep() {
            Event.$emit('moveToSshKeyStep', this.selectedServer);
        },

        storeNewServer(successCallback) {
            if (this.storingServer) {
                return;
            }

            this.storingServer = true;
            this.resetErrors();

            Api().post('servers', this.newServer)
                .then(response => {
                    if (typeof successCallback == 'function') {
                        successCallback();
                    }
                })
                .catch(error => {
                    if (!this.storeErrors(error.response.data.errors)) {
                        if (typeof error.response.data.message == 'string') {
                            this.error = error.response.data.message;
                        } else {
                            this.error = 'Something wrong happened. Refresh the page and try again.';
                        }
                    }

                    this.storingServer = false;
                })
                .finally(() => {
                    // this.storingDatabase = false;
                });
        },

        getExistentServers() {
            if (this.loading) {
                return;
            }

            this.loading = true;

            Api().get('users/current/servers')
                .then(response => {
                    this.servers = response.data;
                    this.serversFetchedOnce = true;
                })
                .catch(error => {
                    console.log(error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        close() {
            Event.$emit('closeAddFile');
        },

        getUserFirstAvailableServerSetup() {
            this.loadingServerSetupToken = true;
            this.autoSetupError = '';

            Api().get('users/current/server-setups/first-available')
                .then(response => {
                    this.serverSetupToken = response.data.setup_token;
                })
                .finally(() => {
                    this.loadingServerSetupToken = false;
                });
        },

        listenEvents() {
            Event.$on('serverSelected', (server) => {
                this.selectedServer = server;
            });
        },

        toggleSetupType() {
            this.autoSetup = !this.autoSetup;

            if (this.autoSetup) {
                this.getUserFirstAvailableServerSetup();
            }
        },
    },

    computed: {
        hasServers() {
            if (this.servers == null) {
                return false;
            }

            if (this.servers.data.length == 0) {
                return false;
            }

            if (!this.serversFetchedOnce) {
                return false;
            }

            return true;
        },

        selectedServerId() {
            if (!this.selectedServer) {
                return null;
            }

            return this.selectedServer.id;
        },

        nextButtonIsDisabled() {
            return (this.actionIsSelected('existent_server') && !this.selectedServer)
                || this.storingServer
            ;
        },

        cancelButtonIsDisabled() {
            return this.storingServer || this.loading;
        },

        setupLabel() {
            if (this.autoSetup) {
                return 'Automatically add the server';
            }
            return 'Manually add the server';
        }
    },

    watch: {
        selectedAction(selectedAction) {
            if (selectedAction == 'existent_server') {
                this.getExistentServers();
            }
        },
    },
}
</script>